import type { LookupServiceProviderType } from "@/types/LookupServiceProviderType";
import type { QsoTableColumnVisibilityInterface } from "@/types/QsoTableColumnVisibilityInterface";
import packageJson from "../package.json";
export const APP_VERSION = packageJson.version;

// https://adif.org/315/ADIF_315.htm#QSOUploadStatus_Enumeration

export const QRZ_UPLOAD_STATUS = {
  UPLOADED: 'Y', //	the QSO has been uploaded to, and accepted by, the online service
  MODIFIED: 'M', // the QSO has been modified since being uploaded to the online service
  DO_NOT_UPLOAD: 'N', // do not upload the QSO to the online service
} as const;

interface BandRange {
  name: string;
  lower: number;
  upper: number;
}
export const LOGBOOK_TEMPLATE_NAMES: Record<string, string> = {
  GENERIC: "Generic",
  POTA: "POTA",
  CUSTOM: "Custom",
  SOTA: "SOTA",
  ARRL_WFD: "Winter FD",
  ARRL_FD: "Field Day"
};

export const LOGBOOK_TEMPLATE_OPTIONS = [
  { value: 'GENERIC', label: LOGBOOK_TEMPLATE_NAMES.GENERIC },
  { value: 'POTA', label: LOGBOOK_TEMPLATE_NAMES.POTA },
  { value: 'SOTA', label: LOGBOOK_TEMPLATE_NAMES.SOTA },
  { value: 'ARRL_WFD', label: LOGBOOK_TEMPLATE_NAMES.ARRL_WFD },
  { value: 'ARRL_FD', label: LOGBOOK_TEMPLATE_NAMES.ARRL_FD },
  { value: 'CUSTOM', label: LOGBOOK_TEMPLATE_NAMES.CUSTOM },
]

export const QSO_TABLE_COLUMN_GROUPS: Record<
  string,
  QsoTableColumnVisibilityInterface
> = {
  ALL: {
    qsoDateTime: true,
    call: true,
    rstSent: false,
    rstRcvd: false,
    name: true,
    state: true,
    gridsquare: false,
    freq: true,
    band: true,
    mode: true,
    potaRef: true,
    sotaRef: true,
    satName: false,
    class: false,
    arrlSect: false,
    qrzcomQsoUploadStatus: true,
  },
  OPERATOR: {
    qsoDateTime: true,
    call: false,
    rstSent: true,
    rstRcvd: true,
    name: false,
    state: false,
    gridsquare: false,
    freq: true,
    band: true,
    mode: true,
    potaRef: true,
    sotaRef: true,
    satName: false,
    class: false,
    arrlSect: false,
    qrzcomQsoUploadStatus: true,
  },
  GENERIC: {
    qsoDateTime: true,
    call: true,
    rstSent: true,
    rstRcvd: true,
    name: false,
    state: true,
    gridsquare: false,
    freq: true,
    band: true,
    mode: true,
    potaRef: false,
    sotaRef: false,
    satName: false,
    class: false,
    arrlSect: false,
    qrzcomQsoUploadStatus: true,
  },
  POTA: {
    qsoDateTime: true,
    call: true,
    rstSent: true,
    rstRcvd: true,
    name: false,
    state: true,
    gridsquare: false,
    freq: true,
    band: true,
    mode: true,
    potaRef: true,
    sotaRef: false,
    satName: false,
    class: false,
    arrlSect: false,
    qrzcomQsoUploadStatus: true,
  },
  SOTA: {
    qsoDateTime: true,
    call: true,
    rstSent: true,
    rstRcvd: true,
    name: false,
    state: true,
    gridsquare: false,
    freq: true,
    band: true,
    mode: true,
    potaRef: false,
    sotaRef: true,
    satName: false,
    class: false,
    arrlSect: false,
    qrzcomQsoUploadStatus: true,
  },
  ARRL_WFD: {
    qsoDateTime: true,
    call: true,
    rstSent: false,
    rstRcvd: false,
    name: false,
    state: false,
    gridsquare: false,
    freq: true,
    band: true,
    mode: true,
    potaRef: false,
    sotaRef: false,
    satName: false,
    class: true,
    arrlSect: true,
    qrzcomQsoUploadStatus: false,
  },
  ARRL_FD: {
    qsoDateTime: true,
    call: true,
    rstSent: false,
    rstRcvd: false,
    name: false,
    state: false,
    gridsquare: false,
    freq: true,
    band: true,
    mode: true,
    potaRef: false,
    sotaRef: false,
    satName: false,
    class: true,
    arrlSect: true,
    qrzcomQsoUploadStatus: false,
  }
};

type TemplateIconAndColor = {
  icon: string;
  color: string;
};

export const LOGBOOK_TEMPLATE_ICONS_AND_COLORS: Record<
  string,
  TemplateIconAndColor
> = {
  UNKNOWN: { icon: "fa-duotone fa-question", color: "red" },
  GENERIC: { icon: "fa-duotone fa-atlas", color: "gray" },
  POTA: { icon: "fa-duotone fa-tree-alt", color: "green" },
  SOTA: { icon: "fa-duotone fa-mountain", color: "blue" },
  ARRL_WFD: { icon: "fa-duotone fa-snowflakes", color: "brand-primary" },
  ARRL_FD: { icon: "fa-duotone fa-sun", color: "yellow" },
  CUSTOM: { icon: "fa-duotone fa-screwdriver-wrench", color: "yellow" },
};

export const LOGBOOK_TEMPLATES = Object.keys(LOGBOOK_TEMPLATE_NAMES);
export const LOGBOOK_ID_PREFIX = "LOGBOOK";
export const LOGBOOK_TEMPLATE_ID_PREFIX = "LOGBOOK_TEMPLATE";

export const PROFILE_ID_PREFIX = "PROFILE";
export const QSO_ID_PREFIX = "QSO";

export const VALID_TIME_REGEX = /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;

export const LOOKUP_SERVICES: Record<LookupServiceProviderType, string> = {
  HAMDB: "HamDB.org",
  QRZ: "QRZ.com",
  HAMQTH: "HamQTH.com",
  OFFLINE: "Internal (Offline)",
};

// Providers a user can pick as their lookup service. OFFLINE is backed by the
// local operator DB (see OfflineLookupService); selecting it routes every lookup
// through that database with no outbound network requests.
export const SELECTABLE_LOOKUP_SERVICES: Partial<
  Record<LookupServiceProviderType, string>
> = {
  HAMDB: LOOKUP_SERVICES.HAMDB,
  QRZ: LOOKUP_SERVICES.QRZ,
  HAMQTH: LOOKUP_SERVICES.HAMQTH,
  OFFLINE: LOOKUP_SERVICES.OFFLINE,
};

// Spot fetch pollers (POTA/SOTA) run every 60s and silently retry single
// failures. After this many *consecutive* failures we surface a connectivity
// warning so the user knows their spot list may be stale.
export const SPOT_FETCH_FAILURE_TOAST_THRESHOLD = 3;

// Watchlist: how long (minutes) before the same matched callsign/park ref can
// produce another toast. Spots poll every 60s, so without a cooldown a single
// ongoing activation would re-notify on every fetch.
export const WATCHLIST_DEFAULT_COOLDOWN_MINUTES = 30;

export const LANGUAGE_OPTIONS: Record<string, string> = {
  en: "English",
};
export const DISTANCE_UNIT_OPTIONS: Record<string, string> = {
  miles: "Miles",
  kilometers: "Kilometers",
};

export const BANDS: string[] = [
  "2200m",
  "630m",
  "560m",
  "160m",
  "80m",
  "60m",
  "40m",
  "30m",
  "20m",
  "17m",
  "15m",
  "12m",
  "10m",
  "8m",
  "6m",
  "4m",
  "2m",
  "1.25m",
  "70cm",
  "33cm",
  "23cm",
  "13cm",
  "9cm",
  "6cm",
  "3cm",
  "1.25cm",
  "6mm",
  "4mm",
  "2.5mm",
  "2mm",
  "1mm",
  "submm",
];

export const BAND_RANGES: BandRange[] = [
  { name: "2200m", lower: 135.7, upper: 137.8 },
  { name: "630m", lower: 472, upper: 479 },
  { name: "560m", lower: 501, upper: 504 },
  { name: "160m", lower: 1800, upper: 2000 },
  { name: "80m", lower: 3500, upper: 4000 },
  { name: "60m", lower: 5330, upper: 5405 },
  { name: "40m", lower: 7000, upper: 7300 },
  { name: "30m", lower: 10100, upper: 10150 },
  { name: "20m", lower: 14000, upper: 14350 },
  { name: "17m", lower: 18000, upper: 18168 },
  { name: "15m", lower: 21000, upper: 21450 },
  { name: "12m", lower: 24890, upper: 24990 },
  { name: "10m", lower: 28000, upper: 29700 },
  { name: "8m", lower: 40000, upper: 45000 },
  { name: "6m", lower: 50000, upper: 54000 },
  { name: "4m", lower: 70000, upper: 70500 },
  { name: "2m", lower: 144000, upper: 148000 },
  { name: "1.25m", lower: 220000, upper: 225000 },
  { name: "70cm", lower: 420000, upper: 450000 },
  { name: "33cm", lower: 902000, upper: 928000 },
  { name: "23cm", lower: 1240000, upper: 1300000 },
  { name: "13cm", lower: 2300000, upper: 2450000 },
  { name: "9cm", lower: 3400000, upper: 3475000 },
  { name: "6cm", lower: 5650000, upper: 5925000 },
  { name: "3cm", lower: 10000000, upper: 10500000 },
  { name: "1.25cm", lower: 24000000, upper: 24250000 },
  { name: "6mm", lower: 47000000, upper: 47200000 },
  { name: "4mm", lower: 75500000, upper: 81000000 },
  { name: "2.5mm", lower: 119980000, upper: 123000000 },
  { name: "2mm", lower: 134000000, upper: 149000000 },
  { name: "1mm", lower: 241000000, upper: 250000000 },
  { name: "submm", lower: 300000000, upper: 7500000000 },
];

/** Separator used to combine MODE and SUBMODE into a single internal string (e.g. "MFSK---FT4") */
export const MODE_SUBMODE_SEPARATOR = "---";

export const MODES: Record<string, string> = {
  SSB: "SSB",
  CW: "CW",
  AM: "AM",
  FM: "FM",
  RTTY: "RTTY",
  FT8: "FT8",
  SSTV: "SSTV",
  C4FM: "C4FM",
  JS8: `MFSK${MODE_SUBMODE_SEPARATOR}JS8`,
  FT4: `MFSK${MODE_SUBMODE_SEPARATOR}FT4`,
  PSK31: `PSK${MODE_SUBMODE_SEPARATOR}PSK31`,
  DIGITALVOICE: "DIGITALVOICE",
  DSTAR: "DSTAR",
};

// Deprecated: MODE_INPUT_OPTIONS was replaced by strongly-typed MODE_OPTIONS

// Shared UI option shapes
export type Option = Readonly<{ value: string; label: string }>;

// Centralized option lists for radio-related fields
export const BAND_OPTIONS: ReadonlyArray<Option> = Object.freeze(
  BANDS.map((band) => ({ value: band, label: band }))
);

export const MODE_OPTIONS: ReadonlyArray<Option> = Object.freeze(
  Object.keys(MODES).map((label) => ({ value: label, label }))
);

// A band's map-dot colors: a solid `fill` with a lighter `stroke` of the same hue,
// following the POTA spots map's DOT_COLORS shape. Both are hardcoded Tailwind shade
// pairs (fill ~ -500/-600, stroke ~ -300) so the rim reads clearly without any runtime
// color math.
export type BandColor = Readonly<{ fill: string; stroke: string }>;

// Microwave bands (cm/mm and shorter) are rarely logged, so they share one muted
// color rather than each claiming a vivid hue. This keeps the palette steered toward
// the high-traffic HF bands where distinct colors actually matter.
const MICROWAVE: BandColor = { fill: "#334155", stroke: "#94a3b8" }; // slate-700 / slate-400

// Per-band display colors for the QSO map (dots, geodesic lines, and band-filter
// chips). Every pair is a -700 fill / -400 stroke (a uniform 3-step gap), matching the
// POTA spots map's "logged" (emerald) and "default" (blue) dots - in fact 40m IS that
// emerald and 17m IS that blue. The vivid jewel tones sit on the workhorse HF bands,
// with the band extremes (LF and microwave) muted/grouped.
export const BAND_COLORS: Readonly<Record<string, BandColor>> = Object.freeze({
  // LF/MF - very rarely logged, muted/grouped
  "2200m": { fill: "#44403c", stroke: "#a8a29e" }, // stone-700 / stone-400
  "630m": { fill: "#44403c", stroke: "#a8a29e" },  // stone-700 / stone-400
  "560m": { fill: "#44403c", stroke: "#a8a29e" },  // stone-700 / stone-400
  // HF - the workhorse bands, smooth warm -> cool jewel gradient
  "160m": { fill: "#b91c1c", stroke: "#f87171" }, // red-700 / red-400
  "80m": { fill: "#c2410c", stroke: "#fb923c" },  // orange-700 / orange-400
  "60m": { fill: "#b45309", stroke: "#fbbf24" },  // amber-700 / amber-400
  "40m": { fill: "#047857", stroke: "#34d399" },  // emerald-700 / emerald-400  (= POTA "logged")
  "30m": { fill: "#0f766e", stroke: "#2dd4bf" },  // teal-700 / teal-400
  "20m": { fill: "#0369a1", stroke: "#38bdf8" },  // sky-700 / sky-400
  "17m": { fill: "#1d4ed8", stroke: "#60a5fa" },  // blue-700 / blue-400        (= POTA "default")
  "15m": { fill: "#4338ca", stroke: "#818cf8" },  // indigo-700 / indigo-400
  "12m": { fill: "#6d28d9", stroke: "#a78bfa" },  // violet-700 / violet-400
  "10m": { fill: "#7e22ce", stroke: "#c084fc" },  // purple-700 / purple-400
  // VHF
  "8m": { fill: "#a21caf", stroke: "#e879f9" },    // fuchsia-700 / fuchsia-400
  "6m": { fill: "#be185d", stroke: "#f472b6" },    // pink-700 / pink-400
  "4m": { fill: "#be123c", stroke: "#fb7185" },    // rose-700 / rose-400
  "2m": { fill: "#a21caf", stroke: "#e879f9" },    // fuchsia-700 / fuchsia-400
  "1.25m": { fill: "#be123c", stroke: "#fb7185" }, // rose-700 / rose-400
  // Microwave (cm/mm and shorter) - grouped under one muted color
  "70cm": MICROWAVE,
  "33cm": MICROWAVE,
  "23cm": MICROWAVE,
  "13cm": MICROWAVE,
  "9cm": MICROWAVE,
  "6cm": MICROWAVE,
  "3cm": MICROWAVE,
  "1.25cm": MICROWAVE,
  "6mm": MICROWAVE,
  "4mm": MICROWAVE,
  "2.5mm": MICROWAVE,
  "2mm": MICROWAVE,
  "1mm": MICROWAVE,
  "submm": MICROWAVE,
});

// Fallback colors for QSOs with no band (or an unrecognized band value).
export const DEFAULT_BAND_COLOR: BandColor = { fill: "#374151", stroke: "#9ca3af" }; // gray-700 / gray-400

export const bandColors = (band?: string | null): BandColor =>
  (band && BAND_COLORS[band]) || DEFAULT_BAND_COLOR;

// Convenience accessors for the fill (dots, lines, chip swatch) and lighter stroke.
export const bandColor = (band?: string | null): string => bandColors(band).fill;
export const bandStrokeColor = (band?: string | null): string => bandColors(band).stroke;


// [{value: "en", label: "English" }],
// 			{ value: "de", label: "Deutsche" },
// 			{ value: "es", label: "Español" },
// 			{ value: "pt", text: "Português" },
// 			{ value: "ru", text: "Pусский" }
// [
//   { label: 'France', value: 'fr', attrs: { disabled: true } },
//   { label: 'Germany', value: 'de', attrs: { disabled: true } },
//   { label: 'Spain', value: 'es', attrs: { disabled: true } },
//   { label: 'Italy', value: 'ie' },
//   { label: 'Greece', value: 'gr', attrs: { disabled: true } },
// ]
/* spell-checker: disable */
export const adifFields = [
  "ADDRESS",
  "ADDRESS_INTL",
  "AGE",
  "ALTITUDE",
  "ANT_AZ",
  "ANT_EL",
  "ANT_PATH",
  "ARRL_SECT",
  "AWARD_GRANTED",
  "AWARD_SUBMITTED",
  "A_INDEX",
  "BAND",
  "BAND_RX",
  "CALL",
  "CHECK",
  "CLASS",
  "CLUBLOG_QSO_UPLOAD_DATE",
  "CLUBLOG_QSO_UPLOAD_STATUS",
  "CNTY",
  "COMMENT",
  "COMMENT_INTL",
  "CONT",
  "CONTACTED_OP",
  "CONTEST_ID",
  "COUNTRY",
  "COUNTRY_INTL",
  "CQZ",
  "CREDIT_SUBMITTED",
  "CREDIT_GRANTED",
  "DARC_DOK",
  "DISTANCE",
  "DXCC",
  "EMAIL",
  "EQ_CALL",
  "EQSL_QSLRDATE",
  "EQSL_QSLSDATE",
  "EQSL_QSL_RCVD",
  "EQSL_QSL_SENT",
  "FISTS",
  "FISTS_CC",
  "FORCE_INIT",
  "FREQ",
  "FREQ_RX",
  "GRIDSQUARE",
  "GRIDSQUARE_EXT",
  "GUEST_OP",
  "HAMLOGEU_QSO_UPLOAD_DATE",
  "HAMLOGEU_QSO_UPLOAD_STATUS",
  "HAMQTH_QSO_UPLOAD_DATE",
  "HAMQTH_QSO_UPLOAD_STATUS",
  "HRDLOG_QSO_UPLOAD_DATE",
  "HRDLOG_QSO_UPLOAD_STATUS",
  "IOTA",
  "IOTA_ISLAND_ID",
  "ITUZ",
  "K_INDEX",
  "LAT",
  "LON",
  "LOTW_QSLRDATE",
  "LOTW_QSLSDATE",
  "LOTW_QSL_RCVD",
  "LOTW_QSL_SENT",
  "MAX_BURSTS",
  "MODE",
  "MS_SHOWER",
  "MY_ALTITUDE",
  "MY_ANTENNA",
  "MY_ANTENNA_INTL",
  "MY_ARRL_SECT",
  "MY_CITY",
  "MY_CITY_INTL",
  "MY_CNTY",
  "MY_COUNTRY",
  "MY_COUNTRY_INTL",
  "MY_CQ_ZONE",
  "MY_DXCC",
  "MY_FISTS",
  "MY_GRIDSQUARE",
  "MY_GRIDSQUARE_EXT",
  "MY_IOTA",
  "MY_IOTA_ISLAND_ID",
  "MY_ITU_ZONE",
  "MY_LAT",
  "MY_LON",
  "MY_NAME",
  "MY_NAME_INTL",
  "MY_POSTAL_CODE",
  "MY_POSTAL_CODE_INTL",
  "MY_POTA_REF",
  "MY_RIG",
  "MY_RIG_INTL",
  "MY_SIG",
  "MY_SIG_INTL",
  "MY_SIG_INFO",
  "MY_SIG_INFO_INTL",
  "MY_SOTA_REF",
  "MY_STATE",
  "MY_STREET",
  "MY_STREET_INTL",
  "MY_USACA_COUNTIES",
  "MY_VUCC_GRIDS",
  "MY_WWFF_REF",
  "NAME",
  "NAME_INTL",
  "NOTES",
  "NOTES_INTL",
  "NR_BURSTS",
  "NR_PINGS",
  "OPERATOR",
  "OWNER_CALLSIGN",
  "PFX",
  "POTA_REF",
  "PRECEDENCE",
  "PROP_MODE",
  "PUBLIC_KEY",
  "QRZCOM_QSO_UPLOAD_DATE",
  "QRZCOM_QSO_UPLOAD_STATUS",
  "QSLMSG",
  "QSLMSG_INTL",
  "QSLRDATE",
  "QSLSDATE",
  "QSL_RCVD",
  "QSL_RCVD_VIA",
  "QSL_SENT",
  "QSL_SENT_VIA",
  "QSL_VIA",
  "QSO_COMPLETE",
  "QSO_DATE",
  "QSO_DATE_OFF",
  "QSO_RANDOM",
  "QTH",
  "QTH_INTL",
  "REGION",
  "RIG",
  "RIG_INTL",
  "RST_RCVD",
  "RST_SENT",
  "RX_PWR",
  "SAT_MODE",
  "SAT_NAME",
  "SFI",
  "SIG",
  "SIG_INTL",
  "SIG_INFO",
  "SIG_INFO_INTL",
  "SILENT_KEY",
  "SKCC",
  "SOTA_REF",
  "SRX",
  "SRX_STRING",
  "STATE",
  "STATION_CALLSIGN",
  "STX",
  "STX_STRING",
  "SUBMODE",
  "SWL",
  "TEN_TEN",
  "TIME_OFF",
  "TIME_ON",
  "TX_PWR",
  "UKSMG",
  "USACA_COUNTIES",
  "VE_PROV",
  "VUCC_GRIDS",
  "WEB",
  "WWFF_REF"
];
/* spell-checker: enable */
